import { useEffect, useState } from "react";
import { collection, limit, onSnapshot, orderBy, query } from "firebase/firestore";
import { PenLine } from "lucide-react";
import { paths, type JobSignoff as Signoff } from "@clevis/shared-types";
import { Button, Card, CardTitle, Input, Label } from "@clevis/shared-ui";
import { db } from "../lib/firebase";
import { call } from "../lib/callFunction";
import { SignaturePad } from "./SignaturePad";

// The customer's sign-off on finished work: they sign on the tech's phone
// that the job is done to their satisfaction. Each company chooses whether
// it's asked at all, offered, or required before "Finish job" (Jobs page).

export function JobSignoff({
  tenantId,
  jobId,
  required,
}: {
  tenantId: string;
  jobId: string;
  /** "Finish job" waits for this signature. */
  required: boolean;
}) {
  const [latest, setLatest] = useState<(Signoff & { id: string }) | null | undefined>(undefined);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [note, setNote] = useState("");
  const [signature, setSignature] = useState<string | null>(null);
  const [showSig, setShowSig] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(
    () =>
      onSnapshot(
        query(collection(db, paths.tenantJobSignoffs(tenantId, jobId)), orderBy("at", "desc"), limit(1)),
        (snap) => setLatest(snap.empty ? null : { id: snap.docs[0]!.id, ...(snap.docs[0]!.data() as Signoff) }),
        () => setLatest(null),
      ),
    [tenantId, jobId],
  );

  async function save() {
    if (!signature) return;
    setBusy(true);
    setError("");
    try {
      await call("recordJobSignoff", { tenantId, jobId, signerName: name.trim(), signaturePngBase64: signature, note: note.trim() });
      setOpen(false);
      setSignature(null);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  if (latest === undefined) return null;
  return (
    <Card>
      <div className="flex items-center justify-between gap-2">
        <CardTitle className="mb-0">Customer sign-off{required ? <span className="ml-1 text-xs font-normal text-brandorange-dark">required to finish</span> : null}</CardTitle>
        {!open ? (
          <Button variant={latest ? "ghost" : "primary"} className="px-2.5 py-1 text-xs" onClick={() => setOpen(true)}>
            <PenLine size={13} /> {latest ? "Sign again" : "Get sign-off"}
          </Button>
        ) : null}
      </div>
      {latest ? (
        <div className="mt-1 text-xs text-ink">
          Signed off by <strong>{latest.signerName}</strong> · {new Date(latest.at).toLocaleString()}
          {latest.note ? <span className="block text-steel">“{latest.note}”</span> : null}
          <button className="ml-1 text-brandorange-dark underline" onClick={() => setShowSig((s) => !s)}>
            {showSig ? "hide signature" : "see signature"}
          </button>
          {showSig ? (
            <img alt={`Signature of ${latest.signerName}`} className="mt-1 h-24 rounded border border-steel/20 bg-white" src={`data:image/png;base64,${latest.signaturePngBase64}`} />
          ) : null}
        </div>
      ) : !open ? (
        <p className="mt-1 text-xs text-steel">When the work's done, hand the customer your phone to sign that it's finished.</p>
      ) : null}

      {open ? (
        <div className="mt-2 space-y-2 rounded-lg bg-cream p-2.5">
          <p className="text-sm text-ink">I confirm the work on this job is complete.</p>
          <div>
            <Label>Customer's name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Full name" />
          </div>
          <div>
            <Label>Sign here</Label>
            <SignaturePad onChange={setSignature} />
          </div>
          <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Comment (optional)" />
          {error ? <p className="text-xs text-danger">{error}</p> : null}
          <div className="flex items-center justify-end gap-2">
            <button className="text-xs text-steel underline" onClick={() => setOpen(false)}>
              Cancel
            </button>
            <Button className="px-3 py-1 text-xs" busy={busy} disabled={!signature || name.trim().length < 2} onClick={() => void save()}>
              Save sign-off
            </Button>
          </div>
        </div>
      ) : null}
    </Card>
  );
}
